import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/Icon';
import { useAuth } from '../features/auth/AuthProvider';
import { tabForPath, tabsFor } from './navigation';
import { useUnreadTotal } from '../features/inquiries/useUnreadTotal';

// Última adreça visitada dins cada pestanya: tornar a una pestanya et deixa on eres.
const lastPathByTab = new Map<string, string>();

/**
 * Carcassa d'app (§10.4). `detail`: pantalla de detall; al mòbil s'amaga la barra de pestanyes
 * (la pantalla porta la seva barra d'accions), a l'escriptori la barra lateral es manté.
 */
export function ShellLayout({ detail = false }: { detail?: boolean }) {
  const { t } = useTranslation('shell');
  const state = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = state.status === 'signedIn';
  const tabs = tabsFor(signedIn ? state.activeRole : null, signedIn, signedIn ? state.modules : []);
  const current = tabForPath(tabs, location.pathname);
  const unread = useUnreadTotal();
  useEffect(() => {
    if (current) lastPathByTab.set(current.id, location.pathname + location.search);
  }, [current, location.pathname, location.search]);

  return (
    <div className={`shell${detail ? ' is-detail' : ''}`}>
      {state.status !== 'loading' && (
        <nav className="tabbar glass" aria-label={t('tabs.label')}>
          {tabs.map((tab) => {
            const active = tab.id === current?.id;
            return (
              <a
                key={tab.id}
                href={tab.path}
                className={`tab${active ? ' is-active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  // Tocar la pestanya activa torna a dalt (o a l'arrel de la pestanya, si ja hi eres).
                  if (active) {
                    // La zona que fa scroll és la de la pantalla, no la finestra (§10.4).
                    const root = document.querySelector<HTMLElement>('[data-scroll-root]');
                    if (root && root.scrollTop > 0) root.scrollTo({ top: 0, behavior: 'smooth' });
                    else if (location.pathname !== tab.path) navigate(tab.path);
                    return;
                  }
                  navigate(lastPathByTab.get(tab.id) ?? tab.path);
                }}
              >
                <span className="tab-icon">
                  <Icon name={tab.icon} size={22} />
                  {tab.id === 'missatges' && unread > 0 && <span className="badge tab-badge" aria-label={t('unreadBadge', { count: unread })}>{unread > 99 ? '99+' : unread}</span>}
                </span>
                <span className="tab-label">{t(tab.label)}</span>
              </a>
            );
          })}
        </nav>
      )}
      <div className="shell-main">
        <Outlet />
      </div>
    </div>
  );
}
